import React from "react";
import { m } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MOD_ITEM, publicFeatureBadge } from "./parts2";
import Module3DIcon from "../../components/marketing/Module3DIcon";
import { feature3dAsset, feature3dColor } from "../../components/marketing/module3dAssets";

// Feature-modal copy lives in ./featureContent.js — one REAL entry per feature card (what +
// capabilities + optional AI flag), all grounded in docs/product/FEATURE_INVENTORY.md.
// The old GENERIC_USES/GENERIC_BENEFITS rotated-pool filler (procedural copy on a diligence surface)
// stays gone; a card missing an entry falls back to its real one-line desc + module + role chips.
// ONE icon language: every marketing feature card uses the project-owned 3D object with the same
// semantic Lucide glyph as its badge/fallback. Product UI continues to use Lucide on its own.


// One compact, premium feature card. The WHOLE card is a real router <Link> — it renders an
// <a href="/modules/:key">, so the 115 module pages are crawlable internal links from the landing
// page and the card keeps ⌘/ctrl/shift/middle-click "open in a new tab" plus the keyboard Enter
// path. (It used to be a <button> that called navigate() — no href, so a crawler saw nothing.)
//
// `onOpen(event)` is the consumer's chance to intercept an activation: return TRUE and the card
// calls preventDefault(), so react-router's own click handler stands down and the consumer's UI
// (the landing module brief) takes over. Return anything else and the anchor navigates normally.
// Bright module `color` drives the (decorative) icon + hover border/glow; badge is the small
// status chip.
function ModuleFeatureCard({
  Icon,
  name,
  desc,
  badge,
  color,
  reduce,
  to,
  onOpen,
  meta,
  actionLabel = "View details",
  testId,
  art,
}) {
  const featureColor = feature3dColor(name, color);
  const statusLabel = publicFeatureBadge(badge);
  return (
    <m.div
      variants={reduce ? undefined : MOD_ITEM}
      className="h-full"
    >
      <Link
        to={to}
        onClick={(event) => { if (onOpen?.(event) === true) event.preventDefault(); }}
        aria-label={`${name} — ${actionLabel}`}
        data-testid={testId}
        className={[
          "mod-chip group relative flex h-full min-h-[168px] w-full cursor-pointer flex-col rounded-2xl border p-4 text-left",
          "transition-[transform,border-color,box-shadow] duration-fast ease-premium",
          // DELTA-7 / DESIGN.md: a scoped property list, never `transition-all`. The hover border
          // needs `!` because .mod-chip's dark-theme rule ([data-theme="dark"] .mod-chip) matches at
          // the same specificity and is authored AFTER @tailwind utilities, so it would win the tie.
          "hover:!border-[color:var(--mc)] hover:shadow-[0_14px_30px_-12px_color-mix(in_srgb,var(--mc)_42%,transparent)]",
          reduce ? "" : "hover:-translate-y-1 active:scale-[0.98]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        ].join(" ")}
        style={{ "--mc": featureColor, "--tw-ring-color": `color-mix(in srgb, ${featureColor} 42%, transparent)` }}
      >
        <span className="flex items-center justify-between mb-2.5">
          <Module3DIcon
            Icon={Icon}
            src={art || feature3dAsset(name)}
            color={featureColor}
            size="md"
            className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:rotate-2"
          />
          {statusLabel && (
            <span className="rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink-2"
              style={{ background: `color-mix(in srgb, ${featureColor} 14%, transparent)` }}>{statusLabel}</span>
          )}
        </span>
        <span className="block text-[13px] font-semibold text-ink leading-tight line-clamp-2">{name}</span>
        <span className="block text-[11px] text-ink-subtle mt-1 leading-snug line-clamp-2 pr-4">{desc}</span>
        <span className="mt-auto block pt-3 pr-16 text-[10px] font-semibold leading-4 text-ink-subtle line-clamp-1">
          {meta || "Open capability details"}
        </span>
        <span className="absolute bottom-3 right-3.5 inline-flex items-center gap-1 text-[10.5px] font-semibold text-accent-active transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true">
          {actionLabel} <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </Link>
    </m.div>
  );
}

// (ModuleSummaryPanel + its MODULE_COVERAGE tables were deleted 2026-08-18 — repo-wide grep
// proved zero consumers, and 5 of its 7 tab accents failed 4.5:1 as 10-12px text. A11Y-10.)

// (The old WHY_VP card list was deleted 2026-08-14 — it had no consumer and silently duplicated
// the why_vp360 pillar copy that lives in WhyVidyapeethSection + core/marketing.py DEFAULTS.)

// Reusable image slot — pass a real `src` later to drop in a photo; until then it renders a clean
// branded placeholder. (User supplies real images; we ship only abstract/blank slots.)

export { ModuleFeatureCard };
